/**
 * ScanLog v4 — bidirectional sync protocol
 *
 * Per spec v0.4 §3.2.
 * Master Log (Google Sheet) = source of truth; IDB = cache + durable write queue.
 *
 * Write path (PWA → Sheet):
 *   - Scans append to `events` with synced=0.
 *   - push() batches ≤50 events, POSTs to doPost, marks synced=1 on 200, bumps
 *     items.syncState "pending" → "synced".
 *   - Failure triggers exponential backoff (30s → 1m → 5m → 30m cap).
 *
 * Read path (Sheet → PWA):
 *   - pull() calls GET ?action=sync&since=<cursor>, upserts rows into `items`,
 *     advances meta.lastSyncCursor.
 *   - Conflict (spec §3.2 P2-1): if local items[b].syncState === 'pending' AND
 *     server row's timestamp is newer, set syncState='conflict' transiently in
 *     the upsert transaction, push a §2.9 toast ("synced with server update"),
 *     and resolve to 'synced' before the txn commits. Server always wins.
 *
 * All fetches use text/plain content-type (v3 CORS lesson, spec §3.3).
 */

import {
	db,
	getMeta,
	setMeta,
	META_KEYS,
	type Item,
	type SyncState
} from '$db/schema';
import { signPostBody, signGetRequest } from './signing';

export interface WebhookConfig {
	url: string;
	/**
	 * HMAC secret shared with Apps Script's HMAC_SECRET script property.
	 * v4.1.5 — sync endpoints require signing (CCQ-292 C-1). Missing secret
	 * is a hard error: we fail closed rather than silently send unsigned.
	 */
	sharedSecret?: string;
}

/**
 * Build GET URL with HMAC ts+nonce+sig appended. Action is signed into payload.
 * Exported so the lookup client can reuse the exact same scheme.
 */
export async function buildSignedGetUrl(
	baseUrl: string,
	secret: string,
	action: string,
	query: Record<string, string>,
	fingerprint: string
): Promise<string> {
	const { ts, nonce, sig } = await signGetRequest(secret, action, fingerprint);
	const params = new URLSearchParams({ action, ...query, ts, nonce, sig });
	return baseUrl + (baseUrl.includes('?') ? '&' : '?') + params.toString();
}

/** Spec §3.2 P2-6 — impl note, not an open question. */
export const BATCH_CAP = 50;

/** Server-side pagination cap per spec §3.3. */
export const PULL_PAGE_CAP = 500;

/** Network timeouts. */
const PUSH_TIMEOUT_MS = 15_000;
const PULL_TIMEOUT_MS = 20_000;

/**
 * v5.3.14 CCQ-327 R3 M-1 — cap the `tabPending` cursor regress at 24h.
 * If the back-date computed from earliest-pending-row.lastScanned would
 * exceed this floor, clamp to `now - this`. Bounds the cost of a
 * permanently-stuck ARRAYFORMULA to one extra 24h sweep per pull,
 * rather than re-walking unbounded history every call.
 */
const TAB_PENDING_REGRESS_CAP_MS = 24 * 60 * 60 * 1000;

/** Exponential backoff ladder in ms (spec §3.2 write path). */
export const BACKOFF_LADDER_MS = [30_000, 60_000, 300_000, 1_800_000] as const;

export interface SyncResult {
	pushed: number;
	pulled: number;
	conflicts: number;
	errors: string[];
	/** ms until next retry when errors non-empty; 0 when success. */
	nextRetryMs: number;
}

export interface PushResult {
	pushed: number;
	errors: string[];
}

export interface PullResult {
	pulled: number;
	conflicts: number;
	conflictBarcodes: string[];
	errors: string[];
}

/**
 * v4.1.5 R4-merge M-2: pure-function decision helpers, exported so the
 * test suite exercises production code paths (not local mirrors). Keep
 * these in lockstep with their call-sites in push() and pull().
 */

/** Shape of the parsed push() response decision. */
export type PushResponseDecision =
	| { kind: 'ok' }
	| { kind: 'serverError'; message: string }
	| { kind: 'nonJson'; excerpt: string };

/**
 * Parse a push() HTTP response body. Mirrors R3-C2 invariant:
 *   - valid JSON with status 'ok'   → { kind: 'ok' }
 *   - valid JSON with status !== 'ok' → { kind: 'serverError' }
 *   - non-JSON body (SSO redirect HTML, CF error page, empty, legacy 'OK')
 *     → { kind: 'nonJson' } — caller MUST NOT flip events.synced on this.
 */
/**
 * v5.3.11 CCQ-327 M3 — known server error codes accepted verbatim.
 *
 * Any `error` field NOT in this set is suppressed at the parse boundary:
 * we pass a stable synthetic message (`internal`, optionally with reqId)
 * to the caller instead of the raw string. This prevents Apps Script
 * stack-trace leakage (sheet IDs, property names, row numbers) from
 * reaching the UI toast or the push-attempt log.
 *
 * Keep aligned with the similarly named constant in driver.svelte.ts —
 * that layer formats known codes for the user; this layer decides which
 * codes survive the boundary at all.
 */
const KNOWN_SERVER_ERROR_CODES = new Set<string>([
	'internal',
	'bad_action',
	'bad_token',
	'unauthorized',
	'proxy_not_configured',
	'proxy_upstream_failure',
	'method_not_allowed',
	'budget_cap',
	'lock_timeout',
	'no_quality_result',
	'config',
	'nonce',
	'ts skew',
	'clock',
	'signature',
	'hmac',
	'no-webhook',
	'no-secret',
	'malformed envelope',
	'non-json',
	'timeout'
]);

function isKnownErrorCode(detail: string): boolean {
	const lower = detail.toLowerCase();
	for (const code of KNOWN_SERVER_ERROR_CODES) {
		if (lower.includes(code)) return true;
	}
	return false;
}

export function parsePushResponse(text: string): PushResponseDecision {
	let parsed: {
		status?: string;
		message?: string;
		error?: string;
		reqId?: string;
	};
	try {
		parsed = JSON.parse(text) as {
			status?: string;
			message?: string;
			error?: string;
			reqId?: string;
		};
	} catch {
		return { kind: 'nonJson', excerpt: text.slice(0, 80) };
	}
	if (parsed.status !== 'ok') {
		// v5.3.7 BDES-SYNC-DIAG: Apps Script uses both `message` and `error`
		// keys depending on the handler (unauthorized path uses `error`,
		// others use `message`). Prior code only read `message`, so auth
		// failures surfaced as the literal fallback 'server reported error'
		// — a true red herring. Read both; `error` first (it's the more
		// specific one on the signed endpoints).
		const rawDetail = parsed.error ?? parsed.message ?? 'server reported error';

		// v5.3.11 CCQ-327 M3: allowlist filter. Unknown codes are rewritten
		// to a stable synthetic envelope that carries only reqId (if any).
		// Known codes — the friendlyError() cases — pass through verbatim.
		if (isKnownErrorCode(rawDetail)) {
			return { kind: 'serverError', message: rawDetail };
		}

		const reqId = typeof parsed.reqId === 'string' ? parsed.reqId : null;
		const safe = reqId
			? `{"error":"internal","reqId":"${reqId}"}`
			: `{"error":"internal"}`;
		return { kind: 'serverError', message: safe };
	}
	return { kind: 'ok' };
}

/** Minimal inputs for the pull() else-branch merge decision. */
export interface MergeElseLocalState {
	lastScanned: string;
	syncState: 'pending' | 'synced' | 'conflict';
}
export interface MergeElseServerRow {
	lastScanned?: string;
}
export interface MergeElseDecision {
	lastScanned: string;
	syncState: 'pending' | 'synced';
}

/**
 * Compute the pull() else-branch merge decision. Mirrors R3-H2 invariant:
 *   - if local has remaining pending events, preserve fresh local
 *     lastScanned + keep syncState='pending'.
 *   - otherwise, apply server-wins on lastScanned and flip to 'synced'.
 * Caller is responsible for counting `remaining` via Dexie query.
 */
export function mergeElseBranchDecision(
	local: MergeElseLocalState,
	row: MergeElseServerRow,
	serverTs: string,
	remaining: number
): MergeElseDecision {
	const lastScanned = remaining > 0 ? local.lastScanned : (row.lastScanned ?? serverTs);
	const syncState: 'pending' | 'synced' = remaining > 0 ? 'pending' : 'synced';
	return { lastScanned, syncState };
}

/**
 * v5.3.12 H3 — build a partial Item update bag from server-returned rich
 * metadata fields. Only includes keys the server actually sent (undefined =
 * leave local field unchanged). Used in three pull() merge paths.
 */
export interface RichMetaServerRow {
	size?: string;
	variant?: string;
	description?: string;
	attributes?: string[];
	keyIngredients?: string[];
	visionConfidence?: number;
	visionModel?: string;
	visionRawText?: string;
}
export function richMetaPatch(row: RichMetaServerRow): Partial<RichMetaServerRow> {
	const patch: Partial<RichMetaServerRow> = {};
	if (row.size !== undefined) patch.size = row.size;
	if (row.variant !== undefined) patch.variant = row.variant;
	if (row.description !== undefined) patch.description = row.description;
	if (row.attributes !== undefined) patch.attributes = row.attributes;
	if (row.keyIngredients !== undefined) patch.keyIngredients = row.keyIngredients;
	if (row.visionConfidence !== undefined) patch.visionConfidence = row.visionConfidence;
	if (row.visionModel !== undefined) patch.visionModel = row.visionModel;
	if (row.visionRawText !== undefined) patch.visionRawText = row.visionRawText;
	return patch;
}

// v4.1.5 R4-merge M-2: PostResponse type removed. Response parsing now
// lives in parsePushResponse() (exported above), which returns a
// discriminated union instead of a loose envelope shape.

/** doGet sync response envelope (spec §3.3).
 *
 * v5.3.12 M1 — `tab` may be null when the server's column K (ARRAYFORMULA)
 *   has not yet evaluated. Client should preserve prior tab in that case.
 * v5.3.12 H3 — Phase 2.2 rich metadata fields (size/variant/description/
 *   attributes/keyIngredients/visionConfidence/visionModel/visionRawText)
 *   are now persisted server-side as a JSON blob in column L and decoded
 *   into top-level fields on the wire.
 */
interface SyncResponse {
	rows: Array<{
		barcode: string;
		product: string;
		brand: string;
		image: string | null;
		source: string;
		sourceUrl: string | null;
		buyLink: string | null;
		tab: string | null;
		category: string;
		timestamp: string; // ISO server-side last-update
		firstScanned?: string;
		lastScanned?: string;
		quantity?: number;
		// v5.3.12 H3 — Phase 2.2 rich metadata. All optional.
		size?: string;
		variant?: string;
		description?: string;
		attributes?: string[];
		keyIngredients?: string[];
		visionConfidence?: number;
		visionModel?: string;
		visionRawText?: string;
	}>;
	cursor: string;
	done?: boolean;
}

/** Utility — ISO-8601 ms UTC with Z suffix, spec §3.1 P3-1. */
export function nowIso(): string {
	return new Date().toISOString();
}

/**
 * v5.3.9 BDES-SYNC-DIAG-3 — same-origin proxy shim.
 * v5.3.11 CCQ-327 H3   — generalized to vision + findImage action classes.
 *
 * iOS Safari standalone-mode PWAs intermittently fail cross-origin POSTs
 * to Apps Script with `TypeError: Load failed` (ITP + 302-redirect bug).
 * Workaround: when we detect the stock Apps Script host AND we're running
 * in a browser (have `window`), rewrite the URL to a same-origin proxy
 * route. Cloudflare Pages Functions at `v4/functions/api/{sync,vision,findImage}.ts`
 * forward the request verbatim to the real Apps Script URL server-side,
 * where ITP doesn't apply. HMAC (for sync) + vision secret (for vision/findImage)
 * are enforced end-to-end through the proxy.
 *
 * Why runtime-detect instead of letting the user change their URL:
 *   - Zero-friction for users already configured
 *   - Shields the Apps Script URL from the client bundle (the proxy
 *     knows it from env var; client only calls `/api/*`)
 *   - Falls through cleanly in SSR/tests where `window` is undefined —
 *     keeps the original URL, HMAC tests unchanged.
 *
 * Route selection by action class:
 *   - 'sync' | 'batch' | ''  (default)  → /api/sync
 *   - 'vision' | 'visionCompare'        → /api/vision
 *   - 'findImage'                       → /api/findImage
 *
 * Exported for testability; tests assert the rewrite is a pure string op.
 */
export type BackendActionClass = 'sync' | 'vision' | 'findImage';

function actionClassToRoute(cls: BackendActionClass): string {
	switch (cls) {
		case 'vision':
			return '/api/vision';
		case 'findImage':
			return '/api/findImage';
		case 'sync':
		default:
			return '/api/sync';
	}
}

/**
 * v5.3.11 CCQ-327 H4 — Proxy-host allowlist.
 *
 * The prior implementation rewrote Apps Script URLs to `<origin>/api/*`
 * whenever `window` was defined. That broke:
 *   - Local dev (localhost:5173) — Pages Functions don't run under `vite dev`
 *   - CF Pages preview deploys where `APPS_SCRIPT_URL` is unset (503)
 *   - User-self-hosted deploys on origins without the Pages Function (404)
 *   - `script.googleusercontent.com` redirect targets were missed entirely
 *
 * Fix: only rewrite on origins we KNOW host the proxy (production +
 * Pages preview subdomain pattern), and also recognize the 302 redirect
 * target `script.googleusercontent.com` as a rewrite candidate for
 * defense-in-depth.
 *
 * Opt-in override for development/self-hosting: set the env var
 * `VITE_USE_BACKEND_PROXY=1` to force-enable on any origin.
 */
const PROXY_ENABLED_ORIGINS = new Set<string>([
	'https://scanlog.pescao.io'
]);

const APPS_SCRIPT_HOSTS = new Set<string>([
	'script.google.com',
	'script.googleusercontent.com'
]);

function isOriginProxyEnabled(origin: string): boolean {
	if (PROXY_ENABLED_ORIGINS.has(origin)) return true;
	// CF Pages preview deploys: https://<hash>.scanlog.pages.dev — allow
	// when the hostname matches the expected pattern. Preview deploys
	// DO have the Pages Functions available.
	try {
		const u = new URL(origin);
		if (u.protocol !== 'https:') return false;
		if (u.hostname.endsWith('.scanlog.pages.dev')) return true;
	} catch {
		/* fall through to explicit opt-in */
	}
	// Explicit opt-in via Vite build-time env var. Used for local dev
	// running `wrangler pages dev` alongside `vite dev`, or for users
	// self-hosting with the Pages Function deployed.
	try {
		const env = (
			import.meta as unknown as { env?: Record<string, string | undefined> }
		).env;
		if (env && env.VITE_USE_BACKEND_PROXY === '1') return true;
	} catch {
		/* no-op in environments without import.meta.env */
	}
	return false;
}

export function effectiveBackendUrl(
	originalUrl: string,
	actionClass: BackendActionClass = 'sync'
): string {
	if (typeof window === 'undefined') return originalUrl;
	try {
		const u = new URL(originalUrl);

		// Idempotence guard: if the URL already points at the local proxy
		// (same origin as window AND pathname starts with /api/), return as-is.
		if (u.origin === window.location.origin && u.pathname.startsWith('/api/')) {
			return originalUrl;
		}

		// Only rewrite the canonical Apps Script hosts.
		if (!APPS_SCRIPT_HOSTS.has(u.host)) return originalUrl;

		// Allowlist gate: only rewrite on origins we know host the proxy.
		if (!isOriginProxyEnabled(window.location.origin)) return originalUrl;

		const route = actionClassToRoute(actionClass);
		const proxied = new URL(route, window.location.origin);
		proxied.search = u.search;
		return proxied.toString();
	} catch {
		return originalUrl;
	}
}

/**
 * Back-compat alias for sync-specific rewriting. Pre-v5.3.11 callers
 * used this name; keep it working (routes to /api/sync).
 */
export function effectiveSyncUrl(originalUrl: string): string {
	return effectiveBackendUrl(originalUrl, 'sync');
}

/**
 * v5.3.11 CCQ-327 M2 — combined timeout + caller signal.
 *
 * Pre-v5.3.11 used `AbortSignal.any([ac.signal, signal])` with a plain
 * fallback to `ac.signal` (timeout only) when `AbortSignal.any` was
 * undefined. That fallback silently dropped the caller's signal, so
 * `SyncDriver.stop()` / navigation abort couldn't cancel in-flight
 * requests on Safari <17.4 or older Androids — they ran until the
 * 15–20s internal timeout instead.
 *
 * Fix: bridge caller abort to the internal AbortController manually
 * when `AbortSignal.any` is unavailable. Every path now honors both
 * signals regardless of platform.
 */
function combineSignalsWithTimeout(
	timeoutMs: number,
	externalSignal?: AbortSignal
): { signal: AbortSignal; cleanup: () => void } {
	const ac = new AbortController();
	const timer = setTimeout(
		() => ac.abort(new DOMException('timeout', 'TimeoutError')),
		timeoutMs
	);

	if (!externalSignal) {
		return { signal: ac.signal, cleanup: () => clearTimeout(timer) };
	}

	// Prefer the native combinator when available — it preserves the
	// original reason objects on abort, which matters for diagnostics.
	const anyFn = (AbortSignal as unknown as {
		any?: (signals: AbortSignal[]) => AbortSignal;
	}).any;
	if (typeof anyFn === 'function') {
		return {
			signal: anyFn([ac.signal, externalSignal]),
			cleanup: () => clearTimeout(timer)
		};
	}

	// Fallback: forward caller abort into the internal controller, so
	// whichever fires first (timeout OR caller stop()) aborts the fetch.
	if (externalSignal.aborted) {
		ac.abort(externalSignal.reason);
	} else {
		const onAbort = () => ac.abort(externalSignal.reason);
		externalSignal.addEventListener('abort', onAbort, { once: true });
		// Cleanup needs to remove the listener too, otherwise a long-lived
		// external signal leaks references to aborted controllers.
		return {
			signal: ac.signal,
			cleanup: () => {
				clearTimeout(timer);
				externalSignal.removeEventListener('abort', onAbort);
			}
		};
	}
	return { signal: ac.signal, cleanup: () => clearTimeout(timer) };
}

/** Abortable fetch with timeout. Text/plain content-type per §3.3. */
async function postWithTimeout(
	url: string,
	body: unknown,
	timeoutMs: number,
	signal?: AbortSignal
): Promise<Response> {
	const { signal: combined, cleanup } = combineSignalsWithTimeout(timeoutMs, signal);
	try {
		return await fetch(effectiveSyncUrl(url), {
			method: 'POST',
			headers: { 'Content-Type': 'text/plain;charset=utf-8' },
			body: JSON.stringify(body),
			signal: combined
		});
	} finally {
		cleanup();
	}
}

async function getWithTimeout(
	url: string,
	timeoutMs: number,
	signal?: AbortSignal
): Promise<Response> {
	const { signal: combined, cleanup } = combineSignalsWithTimeout(timeoutMs, signal);
	try {
		return await fetch(effectiveSyncUrl(url), { method: 'GET', signal: combined });
	} finally {
		cleanup();
	}
}

/**
 * Write path — PWA → Sheet. Spec §3.2.
 *
 * Drains up to BATCH_CAP unsynced events; POSTs as `{events: [...]}` to doPost.
 * On 200: marks events.synced=1 AND items.syncState pending→synced, in one txn.
 * On any error: leaves events pending (they'll retry), returns error detail.
 */
export async function push(
	cfg: WebhookConfig,
	signal?: AbortSignal
): Promise<PushResult> {
	// v4.1.5 C-1: signed requests required. Fail closed if secret missing.
	if (!cfg.sharedSecret) {
		return { pushed: 0, errors: ['no-secret'] };
	}

	// v4.1.5 H-3/M-2: after Dexie v3 bump, synced is pure-boolean and the
	// 'synced' index is authoritative. Prior versions stored booleans AND
	// numbers (IDB compat leakage) — the upgrade step normalizes all rows.
	const pending = await db.events
		.where('synced')
		.equals(0)
		.limit(BATCH_CAP)
		.toArray();

	if (pending.length === 0) return { pushed: 0, errors: [] };

	// Enrich with item snapshots so doPost has everything it needs.
	const barcodes = Array.from(new Set(pending.map((e) => e.barcode)));
	const itemsByBarcode = new Map<string, Item>();
	for (const b of barcodes) {
		const it = await db.items.get(b);
		if (it) itemsByBarcode.set(b, it);
	}

	const bodyPayload = {
		action: 'batch',
		events: pending.map((e) => {
			const it = itemsByBarcode.get(e.barcode);
			return {
				barcode: e.barcode,
				timestamp: e.timestamp,
				product: it?.product ?? '',
				brand: it?.brand ?? '',
				image: it?.image ?? null,
				source: it?.source ?? 'unknown',
				sourceUrl: it?.sourceUrl ?? null,
				buyLink: it?.buyLink ?? null,
				tab: it?.tab ?? 'other',
				category: it?.category ?? '',
				// v5.3.12 H3 — ship Phase 2.2 rich metadata fields up to the
				// server. Only include defined keys so the server-side
				// buildMetadataJson_ helper sees a clean event object.
				...(it?.size !== undefined ? { size: it.size } : {}),
				...(it?.variant !== undefined ? { variant: it.variant } : {}),
				...(it?.description !== undefined ? { description: it.description } : {}),
				...(it?.attributes !== undefined ? { attributes: it.attributes } : {}),
				...(it?.keyIngredients !== undefined ? { keyIngredients: it.keyIngredients } : {}),
				...(it?.visionConfidence !== undefined ? { visionConfidence: it.visionConfidence } : {}),
				...(it?.visionModel !== undefined ? { visionModel: it.visionModel } : {}),
				...(it?.visionRawText !== undefined ? { visionRawText: it.visionRawText } : {})
			};
		})
	};

	// v4.1.5 C-1: sign the payload. Body now carries ts + sig fields.
	const body = await signPostBody(cfg.sharedSecret, bodyPayload);

	try {
		const res = await postWithTimeout(cfg.url, body, PUSH_TIMEOUT_MS, signal);
		if (!res.ok) {
			return { pushed: 0, errors: [`HTTP ${res.status}`] };
		}
		const text = await res.text();
		// v4.1.5 R4 R3-C2 + R4-merge M-2: delegate parse decision to the
		// exported helper so the test suite exercises this exact code path
		// (no local test mirror). Contract:
		//   - nonJson  → early-return error, preserve events.synced=false
		//   - serverError → early-return error, preserve events.synced=false
		//   - ok → fall through into the db.transaction that flips synced=true
		const decision = parsePushResponse(text);
		if (decision.kind === 'nonJson') {
			return {
				pushed: 0,
				errors: [`push: non-JSON response (${decision.excerpt}…)`]
			};
		}
		if (decision.kind === 'serverError') {
			return { pushed: 0, errors: [decision.message] };
		}

		// v4.1.5 C-4/H-2: single transaction covers event flip + item
		// syncState flip. Only transition an item to 'synced' when zero
		// unsynced events remain for that barcode — a scan arriving between
		// request-send and response-settle must still be visible as pending.
		const eventIds = pending.map((e) => e.id).filter((id): id is number => typeof id === 'number');
		await db.transaction('rw', db.events, db.items, async () => {
			for (const id of eventIds) {
				// v4.1.9 CQA-8: numeric 1 — IDB can't index booleans.
				await db.events.update(id, { synced: 1 });
			}
			for (const b of barcodes) {
				const it = await db.items.get(b);
				if (!it) continue;
				// H-2: only flip if no pending events remain for this barcode.
				const remaining = await db.events
					.where('barcode')
					.equals(b)
					.and((e) => e.synced === 0)
					.count();
				if (remaining === 0 && it.syncState === 'pending') {
					await db.items.update(b, { syncState: 'synced' satisfies SyncState });
				}
			}
		});

		// v4.1.5 M-2: prune synced events older than 30 days to keep the
		// events table bounded. Best-effort; failures don't break the response.
		try {
			const cutoff = new Date(Date.now() - 30 * 86_400_000).toISOString();
			await db.events
				.where('synced')
				.equals(1)
				.and((e) => e.timestamp < cutoff)
				.delete();
		} catch {
			// swallow
		}

		// Refresh pendingCount
		await setMeta(META_KEYS.PENDING_COUNT, await countPending());
		return { pushed: pending.length, errors: [] };
	} catch (err) {
		const msg =
			err instanceof Error
				? err.name === 'TimeoutError'
					? 'timeout'
					: err.name === 'AbortError'
						? 'aborted'
						: err.message
				: String(err);
		// v5.3.8 BDES-SYNC-DIAG-2: Safari iOS PWA bug class — "Load failed"
		// doesn't tell us anything actionable. Append environment state so we
		// can distinguish network-flap from SW-intercept from standalone-mode
		// weirdness. Keep it short so the Technical-detail expander stays readable.
		const diag: string[] = [];
		if (err instanceof Error) {
			if (err.name && err.name !== 'Error') diag.push(`name=${err.name}`);
			// err.cause is often the real reason on wrapped DOMExceptions
			const cause = (err as Error & { cause?: unknown }).cause;
			if (cause) diag.push(`cause=${String(cause).slice(0, 60)}`);
		}
		if (typeof navigator !== 'undefined') {
			diag.push(`online=${navigator.onLine}`);
			if ('standalone' in navigator) {
				// iOS PWA detection — true when launched from home screen.
				diag.push(`standalone=${(navigator as Navigator & { standalone?: boolean }).standalone}`);
			}
		}
		const suffix = diag.length ? ` [${diag.join(',')}]` : '';
		return { pushed: 0, errors: [`push failed: ${msg}${suffix}`] };
	}
}

/**
 * Read path — Sheet → PWA. Spec §3.2 + §3.3.
 *
 * Loops until server returns done=true or fewer than PULL_PAGE_CAP rows.
 * Upserts items with aggregate-merge. Implements P2-1 conflict transition.
 */
export async function pull(
	cfg: WebhookConfig,
	signal?: AbortSignal
): Promise<PullResult> {
	let startCursor = await getMeta<string>(
		META_KEYS.LAST_SYNC_CURSOR,
		'1970-01-01T00:00:00.000Z'
	);

	// v5.3.13 CCQ-327 R3 M-2 — if any local rows still have tabPending=true
	// (server returned tab=null on a prior pull because column-K ARRAYFORMULA
	// hadn't spilled yet), back-date the cursor to the earliest such row's
	// lastScanned. The ARRAYFORMULA filling K does NOT bump the row's server
	// timestamp, so a forward-only cursor would miss the tab update forever.
	// One back-date per session is enough: the else-branch will overwrite
	// `tab` and clear `tabPending` once the server returns a non-null tab.
	//
	// v5.3.14 CCQ-327 R3 M-1 — cap regress at 24h. Under genuinely
	// degraded ARRAYFORMULA (formula deleted, sheet-quota throttled,
	// or scripts permission revoked), the prior unbounded back-date
	// would pull the cursor toward epoch every sync — burning bandwidth
	// and re-walking the entire history on each call. Cap floor is
	// `now - 24h`: after a day of pendingness, an unspilled K is an
	// operator-side issue (inspect the sheet, re-run setupTabs), not a
	// re-sync issue. Rows stay flagged `tabPending=true` so they will
	// re-resolve as soon as a new sheet write bumps their `lastScanned`
	// past the (capped) cursor.
	try {
		const pendingRows = await db.items
			.filter((it) => it.tabPending === true)
			.toArray();
		if (pendingRows.length > 0) {
			const initial = pendingRows[0]!.lastScanned;
			const earliest = pendingRows.reduce(
				(min, r) => (r.lastScanned < min ? r.lastScanned : min),
				initial
			);
			// Step the cursor back by 1ms to ensure the earliest pending row
			// is included (server uses strict `>` on lastScanned).
			const earliestMs = Date.parse(earliest);
			if (Number.isFinite(earliestMs)) {
				// v5.3.14 R3 M-1 — never regress further than 24h in the past.
				const floorMs = Date.now() - TAB_PENDING_REGRESS_CAP_MS;
				const cappedMs = Math.max(earliestMs - 1, floorMs);
				const backdated = new Date(cappedMs).toISOString();
				if (backdated < startCursor) {
					startCursor = backdated;
				}
			}
		}
	} catch (err) {
		// Best-effort — if the local scan fails, fall back to the saved cursor.
		console.warn('[scanlog] tabPending re-pull scan failed:', err);
	}
	let cursor = startCursor;
	let totalPulled = 0;
	const conflictBarcodes: string[] = [];
	const errors: string[] = [];

	// v4.1.5 C-1: signed requests required. Fail closed if secret missing.
	if (!cfg.sharedSecret) {
		return { pulled: 0, conflicts: 0, conflictBarcodes: [], errors: ['no-secret'] };
	}

	// Loop pages until drained.
	for (let page = 0; page < 20; page++) {
		const fingerprint = `since=${cursor}`;
		const url = await buildSignedGetUrl(
			cfg.url,
			cfg.sharedSecret,
			'sync',
			{ since: cursor },
			fingerprint
		);
		let resp: SyncResponse;
		try {
			const res = await getWithTimeout(url, PULL_TIMEOUT_MS, signal);
			if (!res.ok) {
				errors.push(`HTTP ${res.status}`);
				break;
			}
			const text = await res.text();
			try {
				resp = JSON.parse(text) as SyncResponse;
			} catch (err) {
				errors.push(`pull parse: ${err instanceof Error ? err.message : String(err)}`);
				break;
			}
		} catch (err) {
			const msg =
				err instanceof Error
					? err.name === 'TimeoutError'
						? 'timeout'
						: err.name === 'AbortError'
							? 'aborted'
							: err.message
					: String(err);
			errors.push(`pull failed: ${msg}`);
			break;
		}

		// v4.1.5 R2 — R-H2: server returns HTTP 200 + {status:'error',error:'…'}
		// for auth failures. The prior check only looked at `rows` presence, so
		// an unauthorized response silently counted as "nothing new" forever.
		// Surface it as a real error so the sync driver can flag it to the user.
		const envelope = resp as { status?: string; error?: string };
		if (envelope.status === 'error') {
			errors.push(`pull: ${envelope.error ?? 'unknown error'}`);
			break;
		}

		// v4.1.5 R4-merge M-1: tighten envelope validation. Previously,
		// a malformed response like `{cursor:'new-cursor'}` (no rows array,
		// no status) would fall through, advance LAST_SYNC_CURSOR via
		// `resp.cursor ?? cursor`, and silently skip any rows the server
		// had queued from the OLD cursor. Now we require BOTH `rows` (array)
		// AND `cursor` (string) before accepting the envelope.
		if (!Array.isArray(resp.rows) || typeof resp.cursor !== 'string') {
			errors.push(
				`pull: malformed envelope (rows=${typeof resp.rows}, cursor=${typeof resp.cursor})`
			);
			break;
		}
		if (resp.rows.length === 0) {
			// Legitimate empty page — advance cursor, exit loop.
			cursor = resp.cursor;
			break;
		}

		// Upsert + conflict transition in one transaction per page.
		// v4.1.5 R2 R-H3: transaction now includes db.events so the conflict
		// branch can check `remaining` unsynced events before flipping state.
		await db.transaction('rw', db.items, db.events, async () => {
			for (const row of resp.rows) {
				const local = await db.items.get(row.barcode);
				const serverTs = row.timestamp ?? row.lastScanned ?? nowIso();

				if (!local) {
					// New item from server — just insert.
					// v5.3.12 M1 — server returns tab=null when its column K
					// (ARRAYFORMULA) is unresolved. For a brand-new local row
					// we have no prior tab to preserve, so 'other' is acceptable
					// as a placeholder.
					//
					// v5.3.13 CCQ-327 R3 M-2 — set tabPending=true so the next
					// pull re-fetches this row independent of lastScanned. The
					// ARRAYFORMULA filling in K does NOT bump the row timestamp,
					// so the standard `serverTs > local.lastScanned` gate would
					// never trigger a re-pull and the row would stay 'other'
					// forever for view-tab purposes.
					const tabIsPending = row.tab == null;
					await db.items.put({
						barcode: row.barcode,
						product: row.product ?? '',
						brand: row.brand ?? '',
						image: row.image ?? null,
						source: row.source ?? 'server',
						sourceUrl: row.sourceUrl ?? null,
						buyLink: row.buyLink ?? null,
						tab: row.tab ?? 'other',
						category: row.category ?? '',
						firstScanned: row.firstScanned ?? serverTs,
						lastScanned: row.lastScanned ?? serverTs,
						quantity: row.quantity ?? 1,
						syncState: 'synced',
						tabPending: tabIsPending ? true : undefined,
						// v5.3.12 H3 — hydrate Phase 2.2 rich fields from server.
						...richMetaPatch(row)
					});
				} else if (local.syncState === 'pending' && serverTs > local.lastScanned) {
					// P2-1 conflict: server newer than local pending. Transient
					// 'conflict', resolve to 'synced' (server wins) in same txn.
					// Surface barcode for toast.
					//
					// v4.1.5 R2 R-H3: only flip back to 'synced' when zero unsynced
					// events remain for this barcode. Symmetric with push() H-2 fix
					// — a concurrent scan between pull-request-send and pull-settle
					// must not be silently dropped from the outbox.
					await db.items.update(row.barcode, { syncState: 'conflict' satisfies SyncState });
					conflictBarcodes.push(row.barcode);
					const remaining = await db.events
						.where('barcode')
						.equals(row.barcode)
						.and((e) => e.synced === 0)
						.count();
					await db.items.update(row.barcode, {
						product: row.product ?? local.product,
						brand: row.brand ?? local.brand,
						image: row.image ?? local.image,
						source: row.source ?? local.source,
						sourceUrl: row.sourceUrl ?? local.sourceUrl,
						buyLink: row.buyLink ?? local.buyLink,
						tab: row.tab ?? local.tab,
						category: row.category ?? local.category,
						lastScanned: row.lastScanned ?? serverTs,
						quantity:
							typeof row.quantity === 'number'
								? Math.max(local.quantity, row.quantity)
								: local.quantity,
						syncState: (remaining === 0 ? 'synced' : 'pending') satisfies SyncState,
						// v5.3.13 R3 M-2 — clear tabPending once the server delivers a non-null tab.
						// v5.3.14 R3 M-2 — symmetric SET on update: if server still returns
						// null tab, we mark tabPending=true regardless of prior local state.
						// Previously this branch only CLEARED on success and preserved any
						// pre-existing local.tabPending — meaning a freshly-updated row
						// whose local.tabPending was undefined would never get flagged for
						// re-fetch, even when the server is still telling us K hasn't
						// spilled. Insert and update branches now agree.
						tabPending: row.tab != null ? undefined : true,
						// v5.3.12 H3 — server-wins on Phase 2.2 rich metadata.
						...richMetaPatch(row)
					});
				} else {
					// Normal aggregate-merge, server wins on fields it specifies.
					//
					// v4.1.5 R4 R3-H2 + R4-merge M-2: decision delegated to
					// exported helper `mergeElseBranchDecision` so the test
					// suite exercises production code, not a local mirror.
					const remaining =
						local.syncState === 'pending'
							? await db.events
									.where('barcode')
									.equals(row.barcode)
									.and((e) => e.synced === 0)
									.count()
							: 0;
					const merge = mergeElseBranchDecision(local, row, serverTs, remaining);
					await db.items.update(row.barcode, {
						product: row.product ?? local.product,
						brand: row.brand ?? local.brand,
						image: row.image ?? local.image,
						source: row.source ?? local.source,
						sourceUrl: row.sourceUrl ?? local.sourceUrl,
						buyLink: row.buyLink ?? local.buyLink,
						tab: row.tab ?? local.tab,
						category: row.category ?? local.category,
						lastScanned: merge.lastScanned,
						quantity:
							typeof row.quantity === 'number'
								? Math.max(local.quantity, row.quantity)
								: local.quantity,
						syncState: merge.syncState satisfies SyncState,
						// v5.3.13 R3 M-2 — clear tabPending once the server delivers a non-null tab.
						// v5.3.14 R3 M-2 — symmetric SET (see other branch above).
						tabPending: row.tab != null ? undefined : true,
						// v5.3.12 H3 — server-wins on Phase 2.2 rich metadata.
						...richMetaPatch(row)
					});
				}
			}
		});

		totalPulled += resp.rows.length;
		cursor = resp.cursor ?? cursor;

		if (resp.done || resp.rows.length < PULL_PAGE_CAP) break;
	}

	if (cursor !== startCursor) {
		await setMeta(META_KEYS.LAST_SYNC_CURSOR, cursor);
	}

	return {
		pulled: totalPulled,
		conflicts: conflictBarcodes.length,
		conflictBarcodes,
		errors
	};
}

/** Full sync cycle — called on online event + 30s interval (spec §3.2). */
export async function sync(
	cfg: WebhookConfig,
	signal?: AbortSignal
): Promise<SyncResult> {
	if (typeof navigator !== 'undefined' && !navigator.onLine) {
		return { pushed: 0, pulled: 0, conflicts: 0, errors: ['offline'], nextRetryMs: 0 };
	}
	if (!cfg.url) {
		return { pushed: 0, pulled: 0, conflicts: 0, errors: ['no-webhook'], nextRetryMs: 0 };
	}
	if (!cfg.sharedSecret) {
		return { pushed: 0, pulled: 0, conflicts: 0, errors: ['no-secret'], nextRetryMs: 0 };
	}

	const pushRes = await push(cfg, signal);
	// If push fails, skip pull this cycle — avoids a pull masking push failures.
	let pullRes: PullResult = { pulled: 0, conflicts: 0, conflictBarcodes: [], errors: [] };
	if (pushRes.errors.length === 0) {
		pullRes = await pull(cfg, signal);
	}

	const errors = [...pushRes.errors, ...pullRes.errors];
	return {
		pushed: pushRes.pushed,
		pulled: pullRes.pulled,
		conflicts: pullRes.conflicts,
		errors,
		nextRetryMs: 0
	};
}

/**
 * How many events are still unsynced (UI badge, spec §3.1 meta.pendingCount).
 *
 * v4.1.5 H-3/M-2: post-schema-v3, `events.synced` is pure boolean and the
 * index is authoritative. Dexie `.equals(0)` matches `false` via the
 * bool↔number coercion contract (Dexie 4 normalizes boolean indexes to
 * 0/1 at the IDB layer). Single source of truth — all callers use this.
 */
export async function countPending(): Promise<number> {
	return db.events.where('synced').equals(0).count();
}
